import {
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";

import { useOptionalToast } from "./Toast";

export interface CopyableProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> {
  children?: ReactNode;
  copiedDurationMs?: number;
  label: string;
  truncate?: boolean;
  value: string;
}

export function Copyable({
  children,
  className,
  copiedDurationMs = 1500,
  label,
  onClick,
  truncate = true,
  value,
  ...props
}: CopyableProps) {
  // Confirmation is a toast wherever one is available: the chip is often the
  // narrowest thing on screen, and swapping its label for "Copied" made it
  // resize and hid the value being confirmed. Outside a ToastProvider
  // (Storybook, a bare test) the local swap remains, so the primitive still
  // gives feedback anywhere.
  const toast = useOptionalToast();
  const [isCopied, setIsCopied] = useState(false);
  const [isCopyFailed, setIsCopyFailed] = useState(false);
  const resetTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current !== undefined) {
        window.clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  async function handleCopy() {
    try {
      await window.navigator.clipboard.writeText(value);
    } catch {
      setIsCopied(false);
      if (toast) {
        toast.error(`Could not copy ${label}.`);
        return;
      }
      setIsCopyFailed(true);
      return;
    }

    setIsCopyFailed(false);

    if (toast) {
      toast.success(`${label} copied`);
      return;
    }

    setIsCopied(true);

    if (resetTimerRef.current !== undefined) {
      window.clearTimeout(resetTimerRef.current);
    }

    resetTimerRef.current = window.setTimeout(() => {
      setIsCopied(false);
      resetTimerRef.current = undefined;
    }, copiedDurationMs);
  }

  const classes = ["marker-copyable"];
  if (truncate) {
    classes.push("marker-copyable--truncate");
  }
  if (isCopied) {
    classes.push("marker-copyable--copied");
  }
  if (isCopyFailed) {
    classes.push("marker-copyable--failed");
  }
  if (className) {
    classes.push(className);
  }

  return (
    <button
      aria-label={`Copy ${label}`}
      className={classes.join(" ")}
      onClick={(event) => {
        onClick?.(event);
        void handleCopy();
      }}
      title={value}
      type="button"
      {...props}
    >
      <span
        aria-hidden={isCopied || isCopyFailed}
        className="marker-copyable__content"
      >
        {children ?? value}
      </span>
      {isCopied ? (
        <span aria-hidden="true" className="marker-copyable__copied">
          Copied
        </span>
      ) : null}
      {isCopyFailed ? (
        <span aria-hidden="true" className="marker-copyable__failed">
          Copy failed
        </span>
      ) : null}
      <span
        aria-live="polite"
        className="marker-copyable__status"
        role="status"
      >
        {isCopied
          ? `${label} copied`
          : isCopyFailed
            ? `Could not copy ${label}.`
            : ""}
      </span>
    </button>
  );
}
